import BarChart from "@rio-cloud/rio-uikit/BarChart";
import Bar from "@rio-cloud/rio-uikit/Bar";
import Spinner from "@rio-cloud/rio-uikit/Spinner";
import { OccurrenceStepEnum, OccurrenceStepLabelEnum } from "../../../../enums/occurrenceStep";
import { formatMilisecondsInDaysHoursMinutes } from "../../../../utils/dateUtils";
import { StepChart } from "./stepChart";
import { OccurrenceWithTimeSpent } from "../../../../models/occurrence";

export interface AverageTimeChartProps {
  showAverageTimeChart: boolean;
  setShowAverageTimeChart: (showAverageTimeChart: boolean) => void;
  activeTab: string;
  setActiveTab: (activeTab: string | null) => void;
  contentWithTooltip: (value: string) => any;
  occurrencesBySteps: { [key in OccurrenceStepEnum]: OccurrenceWithTimeSpent[] };
  finalizedOccurrencesBySteps?: { [key in OccurrenceStepEnum]: OccurrenceWithTimeSpent[] };
  adjustDataWithMinHeight: (data: any[], minHeightPercentage: number) => any[];
  isResponsiveView: boolean;
  chartWidthMultiplier: number;
  defaultChartMinWidth: number;
  dynamicBarSize: number;
  CustomTick: (props: any) => JSX.Element;
  occurrenceStepAvailable: string[];
  stepFilter: OccurrenceStepEnum | null;
  loading?: boolean;
  firstLoad: boolean;
  setChassisListFilter: (chassisListFilter: string[]) => void;
  setStepFilter: (stepFilter: OccurrenceStepEnum | null) => void;
  setChassisListFilterByStep: (chassisListFilterByStep: string[]) => void;
  finishedFilter: string[];
}

export const AverageTimeChart = (props: AverageTimeChartProps) => {
  const CustomLabel = ({ x, y, width, index }: any) => {
    const originalTime = props.occurrencesBySteps[props.activeTab as keyof typeof props.occurrencesBySteps]?.filter(item => item.currentStep)?.[index]?.steps[props.activeTab as keyof typeof props.occurrencesBySteps]?.total ?? 0;
    const label = formatMilisecondsInDaysHoursMinutes(originalTime * 60000);
    return (
      <text
        x={x + width / 2}
        y={y - 6}
        textAnchor="middle"
        fill="#a7afbb"
        fontSize={props.isResponsiveView ? "12px" : "16px"}
      >
        {label}
      </text>
    );
  };

  return (
    <>
      <span className="flex w-full justify-between text-size-16 font-semibold p-8">
        <div>Tempo médio por etapa de reparo</div>
        <div
          className="cursor-pointer"
          onClick={() => props.setShowAverageTimeChart(!props.showAverageTimeChart)}
        >
          <span className={`rioglyph rioglyph-chevron-${props.showAverageTimeChart ? "up" : "down"}`} />
        </div>
      </span>

      {props.showAverageTimeChart &&
        !(props.finishedFilter.length === 1 && props.finishedFilter.includes('true')) &&
        (
          <div className="flex items-center overflow-x-auto flex-nowrap pb-2 mb-4 -mx-4 px-4 snap-x snap-mandatory gap-3">
            {props.occurrenceStepAvailable.map((stepEnum) => {
              const stepKey = OccurrenceStepEnum[stepEnum as keyof typeof OccurrenceStepEnum];
              const isActive = props.activeTab === stepKey;
              const value = ((props.occurrencesBySteps[stepKey]?.filter(item => item.currentStep)?.reduce((acc, curr) => acc + (curr.steps[stepKey]?.total ?? 0), 0) ?? 0) /
                props.occurrencesBySteps[stepKey]?.filter(item => item.currentStep)?.length) *
                60000;

              return (
                <div
                  key={stepEnum}
                  onClick={() => {
                    if (!value) return;
                    if (props.activeTab === stepKey) {
                      props.setActiveTab('');
                      props.setChassisListFilterByStep([]);
                      props.setStepFilter(null);
                    } else {
                      props.setActiveTab(stepKey);
                      props.setChassisListFilterByStep(props.occurrencesBySteps[stepKey]?.filter(item => item.currentStep)?.map(item => item.chassis));
                    }
                  }}
                  className={
                    `flex flex-col justify-center border-b-2 border-t-2 text-left transition bg-gray-100 h-[65px] px-4 snap-start
                        ${!value ? "cursor-not-allowed" : "cursor-pointer"} 
                        ${props.isResponsiveView ? "flex-none min-w-[150px]" : "flex-1"}
                        ${isActive ? "bg-white text-color-primary font-semibold border-b-4 border-b-[#30B4C0]" : "hover:bg-gray-100 text-color-gray"} 
                        ${stepEnum !== props.occurrenceStepAvailable[props.occurrenceStepAvailable.length - 1] ? "border-r-[1px]" : ""}`}
                >
                  <div className="text-size-12 line-clamp-2 h-[25px]">
                    {OccurrenceStepLabelEnum[stepEnum as keyof typeof OccurrenceStepLabelEnum]}
                  </div>
                  <div className="font-semibold text-size-16 truncate mt-2">
                    {props.loading && props.firstLoad ?
                      '-' :
                      props.occurrencesBySteps[stepKey].filter(item => item.currentStep)?.length > 0
                        ? props.contentWithTooltip(formatMilisecondsInDaysHoursMinutes(value))
                        : '0m'}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {props.showAverageTimeChart && props.activeTab === '' && (
        <div className="w-full h-full overflow-x-auto px-8 py-4">
          <StepChart
            adjustDataWithMinHeight={props.adjustDataWithMinHeight}
            data={props.occurrencesBySteps}
            finalizedData={props.finalizedOccurrencesBySteps}
            chartWidthMultiplier={props.chartWidthMultiplier}
            defaultChartMinWidth={props.defaultChartMinWidth}
            dynamicBarSize={props.dynamicBarSize}
            isResponsiveView={props.isResponsiveView}
            loading={props.loading}
            metric="averageTime"
          />
        </div>
      )}

      {props.showAverageTimeChart && props.activeTab !== '' && (
        <div className="w-full h-full overflow-x-auto px-8 py-4">
          {props.loading ? (
            <div className="flex justify-center items-center h-[150px]">
              <Spinner show text="Carregando gráfico..." />
            </div>
          ) : (
            <div
              style={{
                minWidth: "100%",
                width: `${props.chartWidthMultiplier * (props.occurrencesBySteps[props.activeTab as keyof typeof props.occurrencesBySteps]?.filter(item => item.currentStep)?.length || 0)}px`,
                height: props.isResponsiveView ? '150px' : '185px',
              }}
              className="pb-2"
            >
              <BarChart
                data={props.adjustDataWithMinHeight(
                  props.occurrencesBySteps[props.activeTab as keyof typeof props.occurrencesBySteps]?.filter(item => item.currentStep)?.map((item) => {
                    return {
                      label: item.model,
                      time: item.steps[props.activeTab as keyof typeof item.steps]?.total ?? 0,
                    };
                  }) ?? []
                  , 1)}
                dataKey="label"
                xAxisOptions={{
                  dataKey: "label",
                  tick: <props.CustomTick />,
                }}
                tooltip={false}
                showYAxis={false}
                margin={{ bottom: 10, top: 31 }}
                style={{
                  color: "#a7afbb",
                  fontSize: 14,
                  textAlign: "center",
                }}
                width={Math.max(
                  (props.occurrencesBySteps[props.activeTab as keyof typeof props.occurrencesBySteps]?.filter(item => item.currentStep)?.length || 0) *
                  props.chartWidthMultiplier,
                  props.defaultChartMinWidth
                )}
                bars={[
                  <Bar
                    key="bar"
                    isAnimationActive={false}
                    dataKey={(e: any) => e.time}
                    radius={[3, 3, 0, 0]}
                    label={<CustomLabel />}
                    barSize={props.dynamicBarSize}
                    color="color-highlight"
                  />,
                ]}
              />
            </div>
          )}
        </div>
      )}
    </>

  );
};
